import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import type { AxiosError } from 'axios';
import { getFieldErrors, readApiError, DEFAULT_ERROR_MESSAGE } from '@/services/api';
import { useForgotPassword } from '@/features/auth/hooks/useForgotPassword';
import { forgotPasswordSchema, type ForgotPasswordFormInputs } from '@/features/auth/schemas/passwordReset';
import { Form, Input, Label } from '@/shared/components/form';
import { Button } from '@/shared/components/ui/button/Button';

export function ForgotPassword() {
    const mutation = useForgotPassword();
    const [submitted, setSubmitted] = useState(false);

    const {
        control,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<ForgotPasswordFormInputs>({
        resolver: zodResolver(forgotPasswordSchema),
        defaultValues: { email: '' },
    });

    const onSubmit = (data: ForgotPasswordFormInputs) => {
        mutation.mutate(data, {
            onSuccess: () => {
                setSubmitted(true);
            },
            onError: (err) => {
                const status = (err as AxiosError).response?.status;
                const fieldErrors = getFieldErrors(err);

                if (status === 422 && Object.keys(fieldErrors).length > 0) {
                    for (const [field, messages] of Object.entries(fieldErrors)) {
                        setError(field as keyof ForgotPasswordFormInputs, {
                            type: 'server',
                            message: messages[0] ?? 'Invalid',
                        });
                    }
                    return;
                }

                const { message } = readApiError((err as AxiosError).response?.data);
                toast.error(message === DEFAULT_ERROR_MESSAGE ? 'Something went wrong. Please try again.' : message);
            },
        });
    };

    return (
        <div className="flex flex-col flex-1">
            <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
                <div className="mb-5 sm:mb-8">
                    <h1 className="mb-2 font-semibold text-mp-foreground text-title-sm sm:text-title-md">
                        Forgot Password
                    </h1>
                    <p className="text-sm text-mp-muted">
                        Enter your email and we&apos;ll send you a reset link.
                    </p>
                </div>

                {submitted ? (
                    <div role="status" className="p-4 rounded-lg bg-mp-success-content">
                        <p className="text-sm text-mp-success">
                            If an account exists for that email, you&apos;ll receive a reset link shortly.
                        </p>
                    </div>
                ) : (
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <div className="space-y-6">
                            <div>
                                <Label>
                                    Email <span className="text-mp-error">*</span>
                                </Label>
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <Input
                                            type="email"
                                            id="email"
                                            placeholder="Enter your email address"
                                            value={field.value ?? ''}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            hint={fieldState.error?.message}
                                        />
                                    )}
                                />
                            </div>

                            <Button disabled={mutation.isPending} className="w-full" size="sm">
                                {mutation.isPending ? 'Sending...' : 'Send Reset Link'}
                            </Button>

                            {errors.root && (
                                <p className="text-sm text-center text-mp-error">{errors.root.message}</p>
                            )}
                        </div>
                    </Form>
                )}

                <div className="mt-5">
                    <p className="text-sm font-normal text-center text-mp-foreground">
                        <Link to="/login" className="text-mp-link">
                            Back to Sign In
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
